/**
 * standKroki.js — standın GERÇEK ana hattı, çizimin FDS-Backend'e ittiği kenarlardan (backlog #676).
 *
 * ESKİSİ: Duvarlar ve Çıkış Noktaları adımları her stand için aynı sabit L şeklini çiziyordu (çizimle ilgisiz).
 * ŞİMDİ: GET /projects/{id}/drawn-walls her kenar için nerede BAŞLADIĞINI da (startXM, startYM: planın metresi,
 * y aşağı doğru artar) söyler. Kenar i, i. köşeden (i+1). köşeye gider, yani başlangıçlar çokgenin kendisidir.
 * Bu dosya React'ten bağımsızdır (yalnızca veri): cevabı çizilebilir bir ana hatta çevirir, ya da ÇİZİLEMEZ derse
 * null verir. Null = sihirbaz satır listesiyle kalır (eski kayıtlar: plan fdsVector'da bir kez daha kaydedilene
 * kadar kenarların başlangıcı yoktur).
 *
 * ⚠️ HİÇBİR ŞEY TAHMİN EDİLMEZ. Şu durumların her birinde null döner, yarım ya da yanlış bir şekil çizmek yerine:
 *    - bir kenarın başlangıcının x'i ya da y'si yok (kısmen güncellenmiş plan);
 *    - bir şeklin kenar numaraları 0..n-1 değil (bir kenar eksik ya da tekrarlı) ya da 3'ten az kenarı var;
 *    - bir kenarın çizilen uzunluğu, iki köşe arasındaki gerçek uzaklıkla tutmuyor (veri bozuk);
 *    - şeklin alanı ya da sınır kutusunun bir kenarı sıfır.
 * ⚠️ Birden çok şekil: başlangıçlar planın ORTAK koordinat sisteminde olduğundan şekiller birbirine göre gerçek
 *    yerlerinde çizilir; çıkış noktası pini TÜM şekillerin birlikte sınır kutusuna göre 0-1'dir (#495).
 */

/** Bir kenarın çizilen uzunluğu ile köşeleri arasındaki uzaklık en çok bu kadar ayrışabilir (metre, ya da %2). */
const UZUNLUK_TOLERANSI_M = 0.05;
const EPS = 1e-6;

/** Sayı ya da null: backend BigDecimal'i JSON sayısı (mock'ta metin) olarak gönderir. */
function sayi(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const sinirla01 = (v) => Math.min(Math.max(v, 0), 1);
const yuvarla5 = (v) => Math.round(v * 1e5) / 1e5;

/**
 * Bir satırın / kenarın anahtarı: şekil + kenar numarası (bir stand birden çok şekil olabilir, her birinin kendi 0. kenarı vardır).
 * duvarSatirlari.js'teki satır anahtarıyla AYNIDIR; krokide tıklanan kenar bu anahtarla satırı bulur.
 */
export const kenarAnahtari = (shapeId, edgeIndex) => `s${shapeId}-e${edgeIndex}`;

/** Köşeleri sırayla verilen çokgenin işaretli alanı (ayakkabı bağı). İşareti dışarı bakan normali belirler. */
function isaretliAlan(noktalar) {
  let toplam = 0;
  noktalar.forEach((p, i) => {
    const q = noktalar[(i + 1) % noktalar.length];
    toplam += p.x * q.y - q.x * p.y;
  });
  return toplam / 2;
}

/** Kenarın dışarı bakan birim normali: çokgenin işaretli alanına göre (eksen yönünden bağımsız). */
function disaBakanNormal(a, b, alan) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const n = Math.hypot(dx, dy) || 1;
  return alan > 0 ? { x: dy / n, y: -dx / n } : { x: -dy / n, y: dx / n };
}

/**
 * Cevap → çizilebilir ana hat.
 *
 * @param {object|null} veri GET /projects/{id}/drawn-walls cevabı ({drawn, walls[{shapeId, shapeNo, edgeIndex, lengthM, startXM, startYM, facesCorridor}]})
 * @returns {null | {
 *   shapes: Array<{ shapeId: number, shapeNo: number, points: {x:number,y:number}[], centroid: {x:number,y:number},
 *                   sides: Array<{ key: string, shapeId: number, edgeIndex: number, a: {x,y}, b: {x,y}, mid: {x,y},
 *                                  label: {x,y}, lengthM: number, facesCorridor: boolean }> }>,
 *   bounds: { minX:number, minY:number, maxX:number, maxY:number, w:number, h:number },
 *   viewBox: { x:number, y:number, w:number, h:number },
 * }}
 */
export function cizimKonturu(veri) {
  const duvarlar = veri?.walls;
  if (!veri?.drawn || !Array.isArray(duvarlar) || duvarlar.length === 0) return null;

  // Şekillere ayır (backend şekil şekil verir ama sıraya güvenme).
  const sekiller = new Map();
  for (const w of duvarlar) {
    const x = sayi(w?.startXM);
    const y = sayi(w?.startYM);
    const uzunluk = sayi(w?.lengthM);
    if (x == null || y == null || !(uzunluk > 0) || !Number.isInteger(w?.edgeIndex) || w.shapeId == null) return null;
    if (!sekiller.has(w.shapeId)) sekiller.set(w.shapeId, { shapeId: w.shapeId, shapeNo: w.shapeNo ?? sekiller.size + 1, kenarlar: [] });
    sekiller.get(w.shapeId).kenarlar.push({ edgeIndex: w.edgeIndex, x, y, uzunluk, koridor: Boolean(w.facesCorridor) });
  }

  const cikti = [];
  for (const s of sekiller.values()) {
    const kenarlar = [...s.kenarlar].sort((a, b) => a.edgeIndex - b.edgeIndex);
    if (kenarlar.length < 3 || kenarlar.some((k, i) => k.edgeIndex !== i)) return null;
    const noktalar = kenarlar.map((k) => ({ x: k.x, y: k.y }));
    const alan = isaretliAlan(noktalar);
    if (Math.abs(alan) < EPS) return null;

    const merkez = {
      x: noktalar.reduce((t, p) => t + p.x, 0) / noktalar.length,
      y: noktalar.reduce((t, p) => t + p.y, 0) / noktalar.length,
    };
    const kenarKutulari = [];
    for (let i = 0; i < kenarlar.length; i += 1) {
      const k = kenarlar[i];
      const a = noktalar[i];
      const b = noktalar[(i + 1) % noktalar.length];
      const uzaklik = Math.hypot(b.x - a.x, b.y - a.y);
      if (Math.abs(uzaklik - k.uzunluk) > Math.max(UZUNLUK_TOLERANSI_M, k.uzunluk * 0.02)) return null;
      kenarKutulari.push({
        key: kenarAnahtari(s.shapeId, k.edgeIndex), shapeId: s.shapeId, edgeIndex: k.edgeIndex,
        a, b, mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        normal: disaBakanNormal(a, b, alan),
        lengthM: k.uzunluk, facesCorridor: k.koridor,
      });
    }
    cikti.push({ shapeId: s.shapeId, shapeNo: s.shapeNo, points: noktalar, centroid: merkez, sides: kenarKutulari });
  }

  const tum = cikti.flatMap((s) => s.points);
  const minX = Math.min(...tum.map((p) => p.x));
  const maxX = Math.max(...tum.map((p) => p.x));
  const minY = Math.min(...tum.map((p) => p.y));
  const maxY = Math.max(...tum.map((p) => p.y));
  const w = maxX - minX;
  const h = maxY - minY;
  if (!(w > EPS) || !(h > EPS)) return null;

  // Kenar numaraları çizimin dışına, kenarın dışarı bakan tarafına konur; çerçeve onlara yer bırakır.
  const buyuk = Math.max(w, h);
  const bosluk = buyuk * 0.1;
  for (const s of cikti) {
    s.sides = s.sides.map(({ normal, ...kenar }) => ({
      ...kenar,
      label: { x: kenar.mid.x + normal.x * bosluk * 0.6, y: kenar.mid.y + normal.y * bosluk * 0.6 },
    }));
  }
  return {
    shapes: cikti,
    bounds: { minX, minY, maxX, maxY, w, h },
    viewBox: { x: minX - bosluk, y: minY - bosluk, w: w + 2 * bosluk, h: h + 2 * bosluk },
  };
}

/** Ekranda (px) bir tıklamanın SVG kullanıcı koordinatındaki (metre) yeri; svg varsayılan `xMidYMid meet` ile yerleşir. */
export function ekrandanKrokiye(rect, viewBox, clientX, clientY) {
  const olcek = Math.min(rect.width / viewBox.w, rect.height / viewBox.h);
  const kayX = (rect.width - viewBox.w * olcek) / 2;
  const kayY = (rect.height - viewBox.h * olcek) / 2;
  return {
    x: viewBox.x + (clientX - rect.left - kayX) / olcek,
    y: viewBox.y + (clientY - rect.top - kayY) / olcek,
  };
}

/** Krokideki bir nokta → 0-1 {x,y}, TÜM şekillerin sınır kutusuna göre (#495 böyle bekler); kutunun dışı kenara çekilir, 5 ondalık. */
export function krokidenNormal(kontur, nokta) {
  const b = kontur.bounds;
  return {
    x: yuvarla5(sinirla01((nokta.x - b.minX) / b.w)),
    y: yuvarla5(sinirla01((nokta.y - b.minY) / b.h)),
  };
}

/** 0-1 {x,y} → krokideki nokta (metre); krokidenNormal'ın tersi. */
export function normaldenKrokiye(kontur, deger) {
  const b = kontur.bounds;
  return { x: b.minX + sinirla01(deger.x) * b.w, y: b.minY + sinirla01(deger.y) * b.h };
}
